import { FC, ReactNode, useCallback, useContext, useEffect, useMemo, useReducer, useState } from 'react'
import { useMerchant } from './MerchantContext'
import useSWR, { mutate } from 'swr'
import { fetcher, staticOptions } from '../core/api-client'
import { apiURI } from '../utils/constants'
import { parseAddressOptions, parseMandatoryOTP, parseRTOAddressConfigFields } from '../utils/parsers'
import { useLocale } from '../hooks/useLocal'
import { createContext, useContextSelector } from 'use-context-selector'
import { useLoader } from './LoaderContext'
import {
  getAddressOptions,
  getDiscounts,
  setAddressOptions,
  setMerchantAttributes,
  updateOrganizationConfig,
  updateSplitCodConfig
} from 'src/lib/core/api-methods'
import { mapSplitCodResponseToConfig } from '../utils/rto-suite'
import useConfirmOnRouteChanges from '../utils/custom-hooks/useConfirmOnRouteChanges'
import { errorToast, successToast } from '../utils/toasters'
import { useTemporary } from './TemporaryContext'
import {
  SplitCODPayload,
  SplitCodConfigType,
  DiscountConfigType,
  RTOStateType,
  RTOSuiteContextType,
  SplitCodType,
  PrepaidShareType,
  AddressConfigType
} from '../types/rtoSuite'

import { useActivityLogs, EntityName } from '../hooks/useActivityLogs'
import { AddressOptionType } from '../types/merchantAttributes'
import useGTM from '../hooks/useGTM'

export enum PropertyType {
  SplitCodEnable = 'SPLIT_COD_ENABLE',
  C2P = 'C2P',
  MandatoryOtp = 'MANDATORY_OTP',
  ADDRESS_CONFIG = 'ADDRESS_CONFIG'
}
export enum ActionType {
  SET_SPLIT_COD_CONFIG,
  SET_C2P_CONFIG,
  SET_DISCOUNT_CONFIG,
  SET_VERIFY_COD,
  SET_MANDATORY_OTP,
  SET_DISCARD_CHANGES,
  SET_REDUCER_STATE,
  SET_ADDRESS_CONFIG
}

type Action =
  | {
      type: ActionType.SET_SPLIT_COD_CONFIG
      payload: SplitCodConfigType
    }
  | {
      type: ActionType.SET_C2P_CONFIG
      payload: boolean
    }
  | {
      type: ActionType.SET_DISCOUNT_CONFIG
      payload: DiscountConfigType
    }
  | {
      type: ActionType.SET_VERIFY_COD
      payload: string
    }
  | {
      type: ActionType.SET_MANDATORY_OTP
      payload: boolean
    }
  | {
      type: ActionType.SET_REDUCER_STATE
      payload: RTOStateType
    }
  | {
      type: ActionType.SET_DISCARD_CHANGES
      payload: {
        initialRtoState: RTOStateType
        componentsArray?: Array<string>
      }
    }
  | {
      type: ActionType.SET_ADDRESS_CONFIG
      payload: AddressConfigType
    }

export const defaultRtoState: RTOStateType = {
  splitCodConfig: {
    isEnabled: false,
    slabs: [
      {
        minOrderValue: 1,
        maxOrderValue: null,
        prepaidShare: 0,
        prepaidShareType: PrepaidShareType.Flat,
        isCODChargesExcluded: false
      }
    ]
  },
  isC2pEnabled: false,
  discountConfig: {
    isDiscountEdit: false,
    discountId: '',
    discounstDescription: ''
  },
  codVerification: 'NO_VERIFICATION',
  mandatoryOtpEnabled: false,
  addressConfig: {
    addressLine1: {
      isEnabled: true,
      isMandatory: true,
      minCharacterLimit: 10
    },
    addressLine2: {
      isEnabled: true,
      isMandatory: false,
      minCharacterLimit: 10
    },
    email: {
      isEnabled: true,
      isMandatory: true
    },
    gibberishCheckEnabled: false,
    gibberishThreshold: 0.6,
    allowUnicode: true
  }
}

const RTOSuiteContext = createContext<RTOSuiteContextType | undefined>(undefined)
const issuerId = process.env.NEXT_PUBLIC_APP_ISSUER_ID ?? ''
function reducer(state: RTOStateType, action: Action): RTOStateType {
  switch (action.type) {
    case ActionType.SET_SPLIT_COD_CONFIG: {
      return {
        ...state,
        splitCodConfig: {
          ...state?.splitCodConfig,
          isEnabled: action?.payload?.isEnabled,
          slabs: action?.payload?.slabs
        }
      }
    }
    case ActionType.SET_C2P_CONFIG: {
      return {
        ...state,
        isC2pEnabled: action.payload
      }
    }
    case ActionType.SET_DISCOUNT_CONFIG: {
      return {
        ...state,
        discountConfig: action.payload
      }
    }
    case ActionType.SET_DISCARD_CHANGES: {
      const initialState = action.payload?.initialRtoState
      const componentsArray = action.payload?.componentsArray
      const updatedState = (componentsArray ?? [])?.reduce<RTOStateType>((newState: RTOStateType, component) => {
        const tempState = newState
        switch (component) {
          case PropertyType.SplitCodEnable: {
            return {
              ...tempState,
              splitCodConfig: {
                ...tempState?.splitCodConfig,
                isEnabled: initialState?.splitCodConfig?.isEnabled
              }
            }
          }

          case PropertyType.C2P: {
            return {
              ...tempState,
              isC2pEnabled: initialState?.isC2pEnabled,
              codVerification: initialState?.codVerification
            }
          }
          case PropertyType.MandatoryOtp: {
            return {
              ...tempState,
              mandatoryOtpEnabled: initialState?.mandatoryOtpEnabled
            }
          }
          case PropertyType.ADDRESS_CONFIG: {
            return {
              ...tempState,
              addressConfig: {
                ...initialState?.addressConfig
              }
            }
          }
          default: {
            return newState
          }
        }
      }, state)

      return updatedState
    }
    case ActionType.SET_VERIFY_COD: {
      return {
        ...state,
        codVerification: action.payload
      }
    }
    case ActionType.SET_MANDATORY_OTP: {
      return {
        ...state,
        mandatoryOtpEnabled: action.payload
      }
    }
    case ActionType.SET_ADDRESS_CONFIG: {
      return {
        ...state,
        addressConfig: action.payload
      }
    }
    case ActionType.SET_REDUCER_STATE: {
      const reducerState: RTOStateType = action.payload
      return reducerState
    }
    default: {
      return state
    }
  }
}
const propertyValues = ['SPLIT_COD_ENABLE', 'C2P', 'MANDATORY_OTP', 'ADDRESS_CONFIG']
export const RTOSuiteProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { sendActivityLog } = useActivityLogs()
  const { sendGTMTrigger } = useGTM()

  const { setLoaderProperties } = useLoader()
  const { t } = useLocale()
  const { temporary, setTemporaryData } = useTemporary()

  // c2p and cod verification
  const {
    getters: { merchant },
    actions: { setMerchantData }
  } = useMerchant()
  // Mandatory OTP
  const { data: organizationDetails, isValidating } = useSWR(
    Boolean(merchant?.accountId) ? [`/organization/${merchant?.accountId}`, apiURI.AUTH_ENGINE] : null,
    ([url, baseURI]) => fetcher(url, baseURI),
    staticOptions
  )
  // Split COD
  const { data: splitCodData } = useSWR(
    `/split-cod`,
    async (url: string) => {
      const data: any = await fetcher(url, apiURI.FLO_KRATOS_CHECKOUT)

      return data as SplitCodType
    },
    staticOptions
  )

  const callGetAddressConfig = useCallback(async () => {
    try {
      const response: any = await getAddressOptions(merchant?.accountId)
      if (response?.error) {
        errorToast(t('alert_address_options_failed'))
        return
      }
      const configRoot = response?.data?.config
      const parsedAddressOptions = parseAddressOptions(configRoot?.fields)

      const addressConfig: AddressConfigType = parseRTOAddressConfigFields(parsedAddressOptions, configRoot)

      if (response) {
        dispatch({
          type: ActionType.SET_ADDRESS_CONFIG,
          payload: addressConfig
        })

        setInitialRtoState(prev => ({
          ...prev,
          addressConfig: addressConfig
        }))
      }
    } catch (e) {
      console.error('Failed to fetch address config:', e)
    }
  }, [merchant?.accountId])

  const isMandatoryOtpEnabled = parseMandatoryOTP(organizationDetails)

  const [initialRtoState, setInitialRtoState] = useState<RTOStateType>(defaultRtoState)
  const [reducerState, dispatch] = useReducer(reducer, initialRtoState || defaultRtoState)
  useEffect(() => {
    if (temporary) {
      dispatch({
        type: ActionType.SET_REDUCER_STATE,
        payload: temporary
      })
      setTemporaryData(undefined)
    }
  }, [temporary])

  useEffect(() => {
    if (!splitCodData) return
    const splitCodPaymentConfig = mapSplitCodResponseToConfig(splitCodData)

    setInitialRtoState(prevState => ({
      ...prevState,
      splitCodConfig: {
        ...prevState?.splitCodConfig,
        ...splitCodPaymentConfig
      }
    }))
    if (temporary) return
    setSplitCodConfig(splitCodPaymentConfig)
  }, [splitCodData])

  useEffect(() => {
    if (!merchant) return
    callGetDiscounts(1, 0)
    callGetAddressConfig()
  }, [merchant])

  useEffect(() => {
    setInitialRtoState(prevState => ({ ...prevState, mandatoryOtpEnabled: isMandatoryOtpEnabled }))
    if (temporary) return
    setMandatoryOTP(isMandatoryOtpEnabled ?? false)
  }, [isMandatoryOtpEnabled])

  useEffect(() => {
    setInitialRtoState(prevState => ({
      ...prevState,
      isC2pEnabled: merchant?.attributes?.addressConfig?.c2pConfig?.isEnabled ?? defaultRtoState?.isC2pEnabled,
      codVerification: merchant?.attributes?.codVerification ?? defaultRtoState?.codVerification
    }))
    if (temporary) return
    setC2pConfig(Boolean(merchant?.attributes?.addressConfig?.c2pConfig?.isEnabled) ?? defaultRtoState?.isC2pEnabled)
    setVerifyCod((merchant?.attributes?.codVerification as string) ?? defaultRtoState?.codVerification)
  }, [merchant?.attributes])

  useConfirmOnRouteChanges(
    JSON.stringify(initialRtoState) !== JSON.stringify(reducerState),
    () => {
      return new Promise((resolve: any, reject) => {
        if (confirm('You have unsaved changes. Do you want to stay on the page?')) {
          setTemporaryData(reducerState)
          resolve(true)
        } else {
          if (reducerState?.isC2pEnabled && initialRtoState?.isC2pEnabled !== reducerState?.isC2pEnabled) {
            setTemporaryData(reducerState)
            resolve(true)
          }
          reject(false)
        }
      })
    },
    reducerState
  )
  const getDiscountDescription = (c2pDiscount: any) => {
    const placement =
      merchant?.attributes?.addressConfig?.c2pConfig?.link?.enabled &&
      merchant?.attributes?.addressConfig?.c2pConfig?.widget?.enabled
        ? 'order confirmation page and C2P link'
        : merchant?.attributes?.addressConfig?.c2pConfig?.link?.enabled
        ? 'C2P link'
        : 'order confirmation page'
    return `${c2pDiscount?.header} · Via ${placement} `
  }
  const callGetDiscounts = async (limit: number, newPage: number) => {
    try {
      const discountResponse: any = await getDiscounts(
        issuerId,
        merchant?.accountId,
        limit,
        newPage + 1,
        'discounts',
        'POST_ORDER_PLACEMENT',
        '',
        undefined,
        'AMOUNT,BXGY'
      )
      if (discountResponse?.error) {
        errorToast(discountResponse?.error)
        console.error('x-trace-id:', discountResponse.headers?.['x-trace-id'])
      } else {
        if (Boolean(discountResponse?.data?.data?.length)) {
          const c2pDiscount = discountResponse?.data?.data?.[0]
          const discountConfig = {
            isDiscountEdit: Boolean(c2pDiscount?.coupon_id),
            discountId: c2pDiscount?.coupon_id,
            discounstDescription: getDiscountDescription(c2pDiscount)
          }
          setDiscountConfig(discountConfig)
          setInitialRtoState(prevState => ({
            ...prevState,
            discountConfig: {
              isDiscountEdit: Boolean(c2pDiscount?.coupon_id),
              discountId: c2pDiscount?.coupon_id,
              discounstDescription: getDiscountDescription(c2pDiscount)
            }
          }))
        }
      }
    } catch (e) {
      console.error(e)
    }
  }

  const updateSplitCodEnable = async (payload: SplitCODPayload) => {
    try {
      await updateSplitCodConfig(payload)
      // Sending ActivityLog
      const activityLogPayload = {
        entityName: EntityName.RTO_SUITE,
        actionName: 'edited configuration'
      }
      sendActivityLog(activityLogPayload)
      mutate('/split-cod')
    } catch (e) {
      console.error(e)
      errorToast(t('error_split_cod_failed'))
    }
  }
  const updateAccountAttributes = async (isC2pEnabled: boolean, codVerification: string) => {
    try {
      await setMerchantAttributes(
        {
          attributes: {
            c2p: {
              enabled: isC2pEnabled
            },
            cod_verification: codVerification
          }
        },
        merchant?.accountId ?? ''
      )
      setMerchantData(
        {
          ...merchant,
          attributes: {
            ...merchant?.attributes,
            addressConfig: {
              ...merchant?.attributes?.addressConfig,
              c2pConfig: {
                ...merchant?.attributes?.addressConfig?.c2pConfig,
                isEnabled: isC2pEnabled
              }
            },
            codVerification: codVerification
          }
        },
        false
      )
    } catch (e) {
      console.error(e)
      errorToast(t('failed_to_save'))
    }
  }
  const updateOrganization = async (mandatoryOtpEnabled: boolean) => {
    const payload = {
      configuration: {
        force_verification: mandatoryOtpEnabled
      }
    }
    try {
      if (!merchant?.accountId) return
      await updateOrganizationConfig(merchant?.accountId, payload)
      mutate([`/organization/${merchant?.accountId}`, apiURI.AUTH_ENGINE])
    } catch (e) {
      console.error(e)
      errorToast(t('failed_to_save'))
    }
  }

  const updateAddressConfig = async (addressConfig: AddressConfigType) => {
    const payload: AddressOptionType[] = [
      {
        key: 'address1',
        isEnabled: addressConfig.addressLine1.isEnabled,
        isMandatory: addressConfig.addressLine1.isMandatory,
        minCharacterLimit: addressConfig.addressLine1.minCharacterLimit
      },
      {
        key: 'address2',
        isEnabled: addressConfig.addressLine2.isEnabled,
        isMandatory: addressConfig.addressLine2.isMandatory,
        minCharacterLimit: addressConfig.addressLine2.minCharacterLimit
      },
      {
        key: 'email',
        isEnabled: addressConfig.email.isEnabled,
        isMandatory: addressConfig.email.isMandatory
      }
    ]
    try {
      const resp: any = await setAddressOptions(payload, merchant?.accountId, {
        gibberish_check_enabled: addressConfig.gibberishCheckEnabled,
        gibberish_threshold: addressConfig.gibberishThreshold,
        allow_unicode: addressConfig.allowUnicode
      })
      if (resp?.error) {
        errorToast(t('failed_to_save'))
      }
    } catch (error) {
      errorToast(t('failed_to_save'))
    }
  }

  const performActions = async (reducerState: RTOStateType, componentsArray: Array<string>) => {
    const promiseArray: Promise<unknown>[] = []
    componentsArray.forEach(component => {
      switch (component) {
        case PropertyType.SplitCodEnable: {
          if (
            JSON.stringify(reducerState?.splitCodConfig?.isEnabled) !==
            JSON.stringify(initialRtoState?.splitCodConfig?.isEnabled)
          ) {
            const payload = {
              enable: reducerState?.splitCodConfig?.isEnabled
            }
            promiseArray.push(updateSplitCodEnable(payload))
          }
          break
        }

        case PropertyType.C2P: {
          if (
            reducerState?.isC2pEnabled !== initialRtoState?.isC2pEnabled ||
            reducerState?.codVerification !== initialRtoState?.codVerification
          ) {
            promiseArray.push(updateAccountAttributes(reducerState?.isC2pEnabled, reducerState?.codVerification))
            const activityLogPayload = {
              entityName: EntityName.RTO_SUITE,
              actionName: 'edited configuration'
            }
            sendActivityLog(activityLogPayload)
          }
          break
        }
        case PropertyType.MandatoryOtp: {
          if (
            Boolean(merchant?.accountId) &&
            reducerState?.mandatoryOtpEnabled !== initialRtoState?.mandatoryOtpEnabled
          ) {
            promiseArray.push(updateOrganization(reducerState?.mandatoryOtpEnabled))
            const activityLogPayload = {
              entityName: EntityName.RTO_SUITE,
              actionName: 'edited configuration'
            }
            sendActivityLog(activityLogPayload)
          }
          break
        }
        case PropertyType.ADDRESS_CONFIG: {
          if (
            Boolean(merchant?.accountId) &&
            JSON.stringify(reducerState?.addressConfig) !== JSON.stringify(initialRtoState?.addressConfig)
          ) {
            promiseArray.push(
              updateAddressConfig(reducerState.addressConfig).then(() => {
                setInitialRtoState(prev => ({
                  ...prev,
                  addressConfig: reducerState.addressConfig
                }))
              })
            )
            const activityLogPayload = {
              entityName: EntityName.RTO_SUITE,
              actionName: 'edited configuration'
            }
            sendActivityLog(activityLogPayload)
          }
          break
        }
      }
    })
    try {
      setLoaderProperties({ isLoaderVisible: true, message: t('saving') })
      const results = await Promise.allSettled(promiseArray)

      const hasFailure = results.some(r => r.status === 'rejected')
      if (hasFailure) {
        errorToast(t('some_changes_might_not_saved'))
      } else {
        sendGTMTrigger('rto_suite_configuration_edited', 'edit')
        successToast(t('changes_saved'))
      }
    } catch (e) {
      console.error(e)
    } finally {
      setLoaderProperties({ isLoaderVisible: false })
    }
  }

  const setSplitCodConfig = useCallback((splitCodConfig: SplitCodConfigType) => {
    dispatch({
      type: ActionType.SET_SPLIT_COD_CONFIG,
      payload: splitCodConfig
    })
  }, [])

  const setInitialSplitCodConfig = useCallback((splitCodConfig: SplitCodConfigType) => {
    setInitialRtoState(prev => ({
      ...prev,
      splitCodConfig: {
        ...prev.splitCodConfig,
        ...splitCodConfig
      }
    }))
  }, [])
  const setC2pConfig = useCallback((isEnabled: boolean) => {
    dispatch({
      type: ActionType.SET_C2P_CONFIG,
      payload: isEnabled
    })
  }, [])
  const setDiscountConfig = useCallback((discountConfig: DiscountConfigType) => {
    dispatch({
      type: ActionType.SET_DISCOUNT_CONFIG,
      payload: discountConfig
    })
  }, [])

  const setVerifyCod = useCallback((codConfig: string) => {
    dispatch({
      type: ActionType.SET_VERIFY_COD,
      payload: codConfig
    })
  }, [])
  const setMandatoryOTP = useCallback((enable: boolean) => {
    dispatch({
      type: ActionType.SET_MANDATORY_OTP,
      payload: enable
    })
  }, [])
  const setAddressConfig = useCallback((addressConfig: AddressConfigType) => {
    dispatch({
      type: ActionType.SET_ADDRESS_CONFIG,
      payload: addressConfig
    })
  }, [])
  const setDiscardChanges = useCallback(
    (componentsArray = propertyValues) => {
      dispatch({
        type: ActionType.SET_DISCARD_CHANGES,
        payload: {
          initialRtoState: initialRtoState,
          componentsArray: componentsArray
        }
      })
    },
    [initialRtoState]
  )
  const setSaveChanges = useCallback(
    (componentsArray = propertyValues) => {
      performActions(reducerState, componentsArray)
    },
    [reducerState]
  )
  const state = useMemo(
    () => ({
      ...reducerState
    }),
    [reducerState]
  )
  const actions = useMemo(
    () => ({
      setSplitCodConfig,
      setInitialSplitCodConfig,
      setC2pConfig,
      setDiscountConfig,
      setVerifyCod,
      setMandatoryOTP,
      setDiscardChanges,
      setSaveChanges,
      setAddressConfig
    }),
    [
      setSplitCodConfig,
      setInitialSplitCodConfig,
      setC2pConfig,
      setDiscountConfig,
      setVerifyCod,
      setMandatoryOTP,
      setDiscardChanges,
      setSaveChanges,
      setAddressConfig
    ]
  )

  const value: RTOSuiteContextType = useMemo(
    () => ({
      initialRtoState,
      state,
      actions
    }),
    [actions, state, initialRtoState]
  )
  return <RTOSuiteContext.Provider value={value}>{children}</RTOSuiteContext.Provider>
}

export function useRtoSuiteContext<Selected>(selector: (value: RTOSuiteContextType) => Selected) {
  const { t } = useLocale()
  return useContextSelector(RTOSuiteContext, context => {
    if (context === undefined) {
      throw new Error(t('error_RTOSuite_context_not_found'))
    }
    return selector(context)
  })
}
